import { cloneDeep } from 'lodash';

import { getCurrentConference } from '../conference/functions';
import MeetHourJS, { MHConnectionEvents, isFatalMHConnectionError } from '../lib-meet-hour';
import { DEFAULT_RUNTIME_WEBSOCKET_FALLBACK_TIMEOUT_MS, appendURLParam, getBackendSafeRoomName, parseURLParams, readRuntimePreferBoshFlag, recordWebSocketFailure } from '../util';

import { CONNECTION_DISCONNECTED, CONNECTION_ESTABLISHED, CONNECTION_FAILED, CONNECTION_PROPERTIES_UPDATED, CONNECTION_WILL_CONNECT, SET_PREFER_VISITOR } from './actionTypes';
import { MH_CONNECTION_URL_KEY } from './constants';
import logger from './logger';

/**
 * To change prefer visitor in the store. Used later to decide what to request from jicofo on connection.
 *
 * @param {boolean} preferVisitor - The value to set.
 * @returns {{
 *     type: SET_PREFER_VISITOR,
 *     preferVisitor: boolean
 * }}
 */
export function setPreferVisitor(preferVisitor) {
    return {
        type: SET_PREFER_VISITOR,
        preferVisitor
    };
}

/**
 * Create an action for when a connection will connect.
 *
 * @param {JitsiConnection} connection - The {@code JitsiConnection} which will
 * connect.
 * @private
 * @returns {{
 *     type: CONNECTION_WILL_CONNECT,
 *     connection: JitsiConnection
 * }}
 */
function _connectionWillConnect(connection: Object) {
    return {
        type: CONNECTION_WILL_CONNECT,
        connection
    };
}


/**
 * Create an action for when the signaling connection has been lost.
 *
 * @param {JitsiConnection} connection - The {@code JitsiConnection} which
 * disconnected.
 * @private
 * @returns {{
 *     type: CONNECTION_DISCONNECTED,
 *     connection: JitsiConnection
 * }}
 */
export function connectionDisconnected(connection) {
    return {
        type: CONNECTION_DISCONNECTED,
        connection
    };
}


/**
 * Create an action for when the signaling connection has been established.
 *
 * @param {JitsiConnection} connection - The {@code JitsiConnection} which was
 * established.
 * @param {number} timeEstablished - The time at which the
 * {@code JitsiConnection} which was established.
 * @public
 * @returns {{
 *     type: CONNECTION_ESTABLISHED,
 *     connection: JitsiConnection,
 *     timeEstablished: number
 * }}
 */
export function connectionEstablished(
        connection, timeEstablished) {
    return {
        type: CONNECTION_ESTABLISHED,
        connection,
        timeEstablished
    };
}


/**
 * Create an action for when connection properties are updated.
 *
 * @param {Object} properties - The properties which were updated.
 * @private
 * @returns {{
 *     type: CONNECTION_PROPERTIES_UPDATED,
 *     properties: Object
 * }}
 */
function _propertiesUpdate(properties: object) {
    return {
        type: CONNECTION_PROPERTIES_UPDATED,
        properties
    };
}


/**
 * Closes connection.
 *
 * @param {boolean} isRedirect - Indicates if the action has been dispatched as part of visitor promotion.
 *
 * @returns {Function}
 */
export function disconnectAny(isRedirect) {
    return (dispatch, getState) => {
        const { conferenceLeft, conferenceWillLeave } = require('../conference/actions');
        const state = getState();

        // console.log('disconnectANy conference leave');

        // The conference we have already joined or are joining.
        const conference_ = getCurrentConference(state);

        // Promise which completes when the conference has been left and the
        // connection has been disconnected.
        let promise;

        // Leave the conference.
        if (conference_) {
            // console.log('will conference leave');

            // In a fashion similar to MHConference's CONFERENCE_LEFT event
            // (and the respective Redux action) which is fired after the
            // conference has been left, notify the application about the
            // intention to leave the conference.
            dispatch(conferenceWillLeave(conference_, isRedirect));

            promise
                = conference_.leave()
                .catch((error: Error) => {
                    logger.warn(
                        'MHConference.leave() rejected with:',
                        error);

                    // The library lib-meet-hour failed to make the
                    // MHConnection leave. Which may be because
                    // MHConnection thinks it has already left.
                    // Regardless of the failure reason, continue in
                    // meet-hour as if the leave has succeeded.
                    dispatch(conferenceLeft(conference_));
                });
        } else {
            promise = Promise.resolve();
        }


        // Disconnect the connection.
        const { connecting, connection } = state['features/base/connection'];

        // The connection we have already connected or are connecting.
        const connection_ = connection || connecting;

        logger.log('disconnect conference leave');

        if (connection_) {
            promise = promise.then(() => connection_.disconnect());
        } else {
            logger.log('No connection found while disconnecting.');
        }

        return promise;
    };
}

/**
 * Create an action for when the signaling connection could not be created.
 *
 * @param {MHConnection} connection - The {@code MHConnection} which
 * failed.
 * @param {ConnectionFailedError} error - Error.
 * @public
 * @returns {{
 *     type: CONNECTION_FAILED,
 *     connection: MHConnection,
 *     error: ConnectionFailedError
 * }}
 */
export function connectionFailed(
        connection,
        error) {
    const { credentials } = error;

    if (credentials && !Object.keys(credentials).length) {
        error.credentials = undefined;
    }

    return {
        type: CONNECTION_FAILED,
        connection,
        error
    };
}

/**
 * Constructs options to be passed to the constructor of {@code JitsiConnection}
 * based on the redux state.
 *
 * @param {Object} state - The redux state.
 * @returns {Object} The options to be passed to the constructor of
 * {@code JitsiConnection}.
 */
export function constructOptions(state) {
    // Deep clone the options to make sure we don't modify the object in the
    // redux store.
    const options = cloneDeep(state['features/base/config']);

    const { locationURL, preferVisitor } = state['features/base/connection'];
    const params = parseURLParams(locationURL || '');
    const iceServersOverride = params['iceServers.replace'];

    if (iceServersOverride) {
        options.iceServersOverride = iceServersOverride;
    }

    const { bosh, preferBosh, flags } = options;
    let { websocket } = options;

    // TESTING: Only enable WebSocket for some percentage of users.
    if (websocket && navigator.product === 'ReactNative') {
        if ((Math.random() * 100) >= (options?.testing?.mobileXmppWsThreshold ?? 0)) {
            websocket = undefined;
        }
    }

    if (preferBosh || params.preferBosh === '1') {
        websocket = undefined;
    }

    // WebSocket is preferred over BOSH.
    const serviceUrl = websocket || bosh;

    // Append room to the URL's search.
    const { room } = state['features/base/conference'];

    if (serviceUrl && room) {
        const roomName = getBackendSafeRoomName(room);

        options.serviceUrl = appendURLParam(serviceUrl, 'room', roomName ?? '');

        if (options.websocketKeepAliveUrl) {
            options.websocketKeepAliveUrl = appendURLParam(options.websocketKeepAliveUrl, 'room', roomName ?? '');
        }
        if (options.conferenceRequestUrl) {
            options.conferenceRequestUrl = appendURLParam(options.conferenceRequestUrl, 'room', roomName ?? '');
        }
    }

    if (preferVisitor) {
        options.preferVisitor = true;
    }

    // Enable ssrc-rewriting by default.
    if (typeof flags?.ssrcRewritingEnabled === 'undefined') {
        const { ...otherFlags } = flags ?? {};

        options.flags = {
            ...otherFlags,
            ssrcRewritingEnabled: true
        };
    }

    return options;
}

/**
 * Opens new connection.
 *
 * @param {string} [id] - The XMPP user's ID (e.g. {@code user@server.com}).
 * @param {string} [password] - The XMPP user's password.
 * @returns {Function}
 */
export function _connectInternal(id, password) {
    return (dispatch, getState) => {
        const state = getState();
        const options = constructOptions(state);

        logger.log('before _connectVisitor123', options);
        const { locationURL } = state['features/base/connection'];
        const { jwt } = state['features/base/jwt'];
        const roomName = getBackendSafeRoomName(state['features/base/conference'].room);
        const params = parseURLParams(locationURL || '');
        const forceBosh = params.preferBosh === '1';
        const forceWsInitFailure = params.forceWsInitFailure === '1';
        const runtimePreferBosh = readRuntimePreferBoshFlag(config, logger);
        const preferBosh = Boolean(options.preferBosh);
        const shouldTryFallback
            = Boolean(options.websocket && options.bosh && !forceBosh && !preferBosh && !runtimePreferBosh);

        return new Promise((resolve, reject) => {
            let connection;
            let isFallbackAttempt = false;
            let websocketFallbackTimer;
            let activeTransport;
            let attemptStartedAt;

            const configuredFallbackTimeout = Number(config?.runtimeWebSocketFallbackTimeoutMs);
            const websocketFallbackTimeoutMs = Number.isFinite(configuredFallbackTimeout) && configuredFallbackTimeout > 0
                ? configuredFallbackTimeout
                : DEFAULT_RUNTIME_WEBSOCKET_FALLBACK_TIMEOUT_MS;

            _startConnectionAttempt(forceBosh || preferBosh || runtimePreferBosh ? 'bosh' : 'websocket');

            /**
             * Clears pending websocket fallback timer.
             *
             * @returns {void}
             */
            function _clearWebsocketFallbackTimer() {
                clearTimeout(websocketFallbackTimer);
                websocketFallbackTimer = undefined;
            }

            /**
             * Immediately retries with BOSH if websocket is blocked.
             *
             * @param {string} reason - Human readable reason for logs.
             * @returns {boolean}
             */
            function _triggerImmediateBoshFallback(reason) {
                if (isFallbackAttempt || !shouldTryFallback) {
                    return false;
                }

                _clearWebsocketFallbackTimer();
                recordWebSocketFailure(config, logger);
                logger.warn(`${reason}; retrying with BOSH/http-bind.`);

                const previousConnection = connection;

                unsubscribe();
                isFallbackAttempt = true;

                try {
                    previousConnection?.disconnect?.();
                } catch (disconnectError) {
                    logger.warn('Failed to disconnect websocket attempt before BOSH fallback.', disconnectError);
                }

                _startConnectionAttempt('bosh');

                return true;
            }

            /**
             * Starts a connection attempt with the selected transport.
             *
             * @param {'websocket'|'bosh'} transport - The transport to try.
             * @returns {void}
             */
            function _startConnectionAttempt(transport) {
                _clearWebsocketFallbackTimer();

                activeTransport = transport;
                attemptStartedAt = Date.now();

                const attemptOptions = cloneDeep(options);
                const baseServiceUrl = transport === 'bosh'
                    ? options.bosh
                    : options.websocket || options.bosh;

                if (!baseServiceUrl) {
                    reject(new Error('No XMPP service URL is configured.'));

                    return;
                }

                attemptOptions.serviceUrl = roomName
                    ? appendURLParam(baseServiceUrl, 'room', roomName ?? '')
                    : baseServiceUrl;

                if (attemptOptions.websocketKeepAliveUrl && roomName) {
                    attemptOptions.websocketKeepAliveUrl
                        = appendURLParam(attemptOptions.websocketKeepAliveUrl, 'room', roomName ?? '');
                }
                if (attemptOptions.conferenceRequestUrl && roomName) {
                    attemptOptions.conferenceRequestUrl
                        = appendURLParam(attemptOptions.conferenceRequestUrl, 'room', roomName ?? '');
                }

                try {
                    if (transport === 'websocket' && forceWsInitFailure && !isFallbackAttempt) {
                        throw new Error('Injected websocket initialization failure (forceWsInitFailure=1).');
                    }

                    connection = new MeetHourJS.MHConnection(attemptOptions.appId, jwt, attemptOptions);
                    connection[MH_CONNECTION_URL_KEY] = locationURL;

                    dispatch(_connectionWillConnect(connection));
                    logger.log('connectVisitor123', connection, jwt, transport);

                    connection.addEventListener(
                        MHConnectionEvents.CONNECTION_DISCONNECTED,
                        _onConnectionDisconnected);
                    connection.addEventListener(
                        MHConnectionEvents.CONNECTION_ESTABLISHED,
                        _onConnectionEstablished);
                    connection.addEventListener(
                        MHConnectionEvents.CONNECTION_FAILED,
                        _onConnectionFailed);
                    connection.addEventListener(
                        MHConnectionEvents.CONNECTION_REDIRECTED,
                        _onConnectionRedirected);
                    connection.addEventListener(
                        MHConnectionEvents.PROPERTIES_UPDATED,
                        _onPropertiesUpdate);

                    if (transport === 'websocket' && shouldTryFallback && !isFallbackAttempt) {
                        websocketFallbackTimer = setTimeout(() => {
                            _triggerImmediateBoshFallback(
                                `Visitor websocket attempt timed out after ${websocketFallbackTimeoutMs}ms`
                            );
                        }, websocketFallbackTimeoutMs);
                    }

                    // Log ALL events on the connection
                    Object.values(MHConnectionEvents).forEach(eventName => {
                        connection.addEventListener(eventName, (...args) => {
                            logger.log(`CONNECTION EVENT FIRED: ${eventName}`, args);
                        });
                    });

                    connection.connect({
                        id,
                        password,
                        name: roomName
                    });
                } catch (error) {
                    logger.warn(`Connection attempt initialization failed on ${transport}.`, error);

                    if (transport === 'websocket'
                        && _triggerImmediateBoshFallback('Visitor WebSocket initialization failed')) {
                        return;
                    }

                    unsubscribe();
                    reject(error);
                }
            }

            /**
             * Unsubscribe the connection instance from
             * {@code CONNECTION_DISCONNECTED} and {@code CONNECTION_FAILED} events.
             *
             * @returns {void}
             */
            function unsubscribe() {
                if (!connection) {
                    return;
                }

                _clearWebsocketFallbackTimer();

                connection.removeEventListener(
                    MHConnectionEvents.CONNECTION_DISCONNECTED, _onConnectionDisconnected);
                connection.removeEventListener(MHConnectionEvents.CONNECTION_FAILED, _onConnectionFailed);
                connection.removeEventListener(MHConnectionEvents.CONNECTION_ESTABLISHED, _onConnectionEstablished);
                connection.removeEventListener(MHConnectionEvents.CONNECTION_REDIRECTED, _onConnectionRedirected);
                connection.removeEventListener(MHConnectionEvents.PROPERTIES_UPDATED, _onPropertiesUpdate);
            }

            /**
             * Dispatches {@code CONNECTION_DISCONNECTED} action when connection is
             * disconnected.
             *
             * @private
             * @returns {void}
             */
            function _onConnectionDisconnected() {
                unsubscribe();
                dispatch(connectionDisconnected(connection));
                resolve(connection);
            }

            /**
             * Rejects external promise when connection fails.
             *
             * @param {MHConnectionErrors} err - Connection error.
             * @param {string} [message] - Error message supplied by lib-meet-hour.
             * @param {Object} [credentials] - The invalid credentials that were
             * used to authenticate and the authentication failed.
             * @param {string} [credentials.jid] - The XMPP user's ID.
             * @param {string} [credentials.password] - The XMPP user's password.
             * @param {Object} details - Additional information about the error.
             * @private
             * @returns {void}
             */
            function _onConnectionFailed(
                    err: string,
                    message: string,
                    credentials: any,
                    details: Object) {
                // Only transport failures earn a BOSH retry. PASSWORD_REQUIRED is the server answering over
                // a websocket that works, so retrying cannot change the answer - and the retry path calls
                // recordWebSocketFailure(), which would charge the session a websocket failure for every
                // password-protected or lobby-enabled room. See the matching guard in connection.js.
                if (isFatalMHConnectionError(err)
                        && _triggerImmediateBoshFallback('Visitor connection failed on WebSocket')) {
                    return;
                }

                unsubscribe();

                dispatch(connectionFailed(connection, {
                    credentials,
                    details,
                    name: err,
                    message
                }));

                reject(err);
            }

            /**
             * Resolves external promise when connection is established.
             *
             * @private
             * @returns {void}
             */
            function _onConnectionEstablished(...connectionParams) {
                connection.removeEventListener(MHConnectionEvents.CONNECTION_ESTABLISHED, _onConnectionEstablished);

                // Transport telemetry - see the matching log in connection.js.
                logger.info('[Transport] XMPP connection established'
                    + ` transport=${activeTransport}`
                    + ` establishMs=${Date.now() - attemptStartedAt}`
                    + ` isFallbackAttempt=${isFallbackAttempt}`
                    + ` runtimePreferBosh=${runtimePreferBosh}`
                    + ` wsFallbackTimeoutMs=${websocketFallbackTimeoutMs}`);

                dispatch(connectionEstablished(connection, Date.now()));
                logger.log('connection established', connectionParams);

                resolve(connection);
            }

            /**
             * Connection was redirected.
             *
             * @param {string|undefined} vnode - The vnode to connect to.
             * @param {string} focusJid - The focus jid to use.
             * @param {string|undefined} username - The username to use when joining. This is after promotion from
             * visitor to main participant.
             * @private
             * @returns {void}
             */
            function _onConnectionRedirected(vnode, focusJid, username) {
                logger.log('_onConnectionRedirected CALLED:', {
                    vnode,
                    focusJid,
                    username });
                connection.removeEventListener(MHConnectionEvents.CONNECTION_REDIRECTED, _onConnectionRedirected);
                logger.log('connection redirected to ', vnode, focusJid, username);
                dispatch(require('../conference/actions').redirect(vnode, focusJid, username));
            }

            /**
             * Connection properties were updated.
             *
             * @param {Object} properties - The properties which were updated.
             * @private
             * @returns {void}
             */
            function _onPropertiesUpdate(properties: object) {
                dispatch(_propertiesUpdate(properties));
            }
        });
    };
}
